import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import fetchToBackend from "@/utils/fetchToBackend";
import { workAdvisoryKeys } from "@/lib/query-keys";
import type {
  BidDocumentState,
  ReportPreparation,
} from "@/types/workadvisory";

/**
 * El estado de las bases de una obra. Mientras se están leyendo se repregunta
 * sola: el backend responde en el acto y hace el trabajo por detrás, así que
 * esta consulta es la única forma de saber cuándo terminó.
 */
export function useBidDocumentState(contractingId: number, live = false) {
  return useQuery({
    queryKey: workAdvisoryKeys.bases(contractingId),
    queryFn: async () => {
      const res = await fetchToBackend<{ data: BidDocumentState }>(
        `/admin/work-advisory/bases/${contractingId}`,
      );
      return res.data;
    },
    // `live` es "el lote está corriendo": una obra que todavía no arrancó no se
    // repregunta sola —su estado no cambia— pero durante el lote sí, porque le
    // va a tocar y hay que ver el chip encenderse.
    refetchInterval: (query) =>
      live || query.state.data?.status === "EN_PROCESO" ? 10_000 : false,
  });
}

/** Arranca la descarga y lectura de las bases. Devuelve en cuanto arranca. */
export function usePrepareBidDocuments() {
  const qc = useQueryClient();
  return useMutation({
    // `force` relee unas bases ya leídas: es lo que hace el botón de rehacer.
    mutationFn: async ({
      contractingId,
      force,
    }: {
      contractingId: number;
      force?: boolean;
    }) => {
      const res = await fetchToBackend<{ data: BidDocumentState }>(
        "/admin/work-advisory/bases",
        { method: "POST", body: JSON.stringify({ contractingId, force }) },
      );
      return res.data;
    },
    onSuccess: (_data, { contractingId }) =>
      qc.invalidateQueries({ queryKey: workAdvisoryKeys.bases(contractingId) }),
  });
}

/**
 * El progreso de preparar el informe entero. Se repregunta sola mientras corre:
 * son decenas de minutos y el backend no puede avisar.
 */
export function useReportPreparation(reportId: number | undefined) {
  return useQuery({
    queryKey: workAdvisoryKeys.preparation(reportId ?? 0),
    enabled: Boolean(reportId),
    queryFn: async () => {
      const res = await fetchToBackend<{ data: ReportPreparation }>(
        `/admin/work-advisory/reports/${reportId}/preparation`,
      );
      return res.data;
    },
    refetchInterval: (query) => (query.state.data?.running ? 15_000 : false),
  });
}

/** Arranca la preparación de todas las obras del informe. */
export function usePrepareReport() {
  const qc = useQueryClient();
  return useMutation({
    // `force` rehace todo desde cero —relee las bases y vuelve a cruzar— y es lo
    // único que se admite sobre un informe ya publicado.
    mutationFn: async ({
      reportId,
      force,
    }: {
      reportId: number;
      force?: boolean;
    }) => {
      const res = await fetchToBackend<{ data: ReportPreparation }>(
        `/admin/work-advisory/reports/${reportId}/prepare`,
        { method: "POST", body: JSON.stringify({ force }) },
      );
      return res.data;
    },
    onSuccess: (_data, { reportId }) =>
      qc.invalidateQueries({
        queryKey: workAdvisoryKeys.preparation(reportId),
      }),
  });
}

/**
 * Sube un PDF de bases optimizado a mano. Va en base64 por el body, igual que la
 * subida manual de archivos del admin. El tope es el de la lectura (40 MB), que
 * en base64 viaja a ~53 y entra en el `express.json` de 60.
 */
export function useUploadBidDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      contractingId,
      file,
    }: {
      contractingId: number;
      file: File;
    }) => {
      const base64Data = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () =>
          resolve(String(reader.result).split(",")[1] ?? "");
        reader.onerror = () => reject(new Error("No se pudo leer el archivo"));
        reader.readAsDataURL(file);
      });

      const res = await fetchToBackend<{ data: BidDocumentState }>(
        "/admin/work-advisory/bases/upload",
        {
          method: "POST",
          body: JSON.stringify({ contractingId, fileName: file.name, base64Data }),
        },
      );
      return res.data;
    },
    onSuccess: (_data, { contractingId }) =>
      qc.invalidateQueries({ queryKey: workAdvisoryKeys.bases(contractingId) }),
  });
}
